<template>
  <article
    class="points-level-card"
    :class="`points-level-card--${props.variant}`"
    :style="card.cardStyle"
  >
    <div
      v-if="card.isCurrent"
      class="points-level-card__tag"
      :style="{ background: card.tagColor, color: card.tagTextColor }"
    >
      {{ props.tagText }}
    </div>

    <div class="points-level-card__body">
      <div
        class="points-level-card__title"
        :class="{
          'points-level-card__title--partner':
            props.variant === 'mine' && card.isPartner,
        }"
      >
        <AppSvgIcon
          v-if="card.svgIcon"
          class="points-level-card__icon"
          :name="card.svgIcon"
          color="var(--wp-color-brand-1)"
        />
        <app-image
          v-else
          class="points-level-card__icon"
          :src="card.icon"
          alt=""
        />
        <div data-strong :style="{ color: card.primaryColor }">
          {{ card.label }}
        </div>
      </div>

      <div
        v-if="card.isShowBar || props.preserveProgressSpace"
        class="points-level"
        :class="{ 'points-level--hidden': card.isShowBar === false }"
      >
        <div class="points-level-card__bar" :style="{ background: card.barBg }">
          <i
            :style="{
              width: `${card.progress}%`,
              background: card.barColor,
            }"
          />
        </div>
        <div class="hint" :style="{ color: card.descColor }">
          {{ card.hint }}
        </div>
      </div>

      <div
        class="points-level-card__foot"
        :class="{ 'points-level-card__foot--four': metrics.length === 4 }"
      >
        <div
          v-for="item in metrics"
          :key="item.label"
          class="points-level-card_list"
          :style="{ color: card.descColor }"
          @click="handleMetricClick(item)"
        >
          <div class="points-level-card_item">
            <div class="points-level-card_item-label">
              <span>{{ item.label }}</span>
              <span
                v-if="item.icon"
                class="points-level-card_item-icon"
                aria-hidden="true"
                >i</span
              >
            </div>
            <div
              :class="{
                'points-level-card_item-value--zero': item.isCount,
              }"
            >
              {{ item.value }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>

  <van-popup
    v-model:show="showPurchaseCommissionPopup"
    teleport="body"
    position="center"
    round
    class="purchase-commission-popup"
    :class="{
      'purchase-commission-popup--wallet': props.variant === 'wallet',
    }"
    overlay-class="purchase-commission-overlay"
    :close-on-click-overlay="false"
  >
    <section class="purchase-commission-dialog">
      <h2>{{ t("invite.purchaseCommissionTitle") }}</h2>
      <p class="purchase-commission-dialog__intro">
        {{ t("invite.purchaseCommissionIntro") }}
      </p>

      <div class="purchase-commission-dialog__item">
        <p>
          <span class="purchase-commission-dialog__time">{{
            t("invite.purchaseCommissionTip1")
          }}</span
          >{{ t("invite.purchaseCommissionTip1Desc") }}
        </p>
      </div>
      <div class="purchase-commission-dialog__item">
        <p>
          <span class="purchase-commission-dialog__time">{{
            t("invite.purchaseCommissionTip2")
          }}</span
          >，{{ t("invite.purchaseCommissionTip2Desc") }}
        </p>
      </div>
      <div class="purchase-commission-dialog__item">
        <h3>
          <span class="purchase-commission-dialog__time">{{
            t("invite.purchaseCommissionTip3Time")
          }}</span
          >{{ t("invite.purchaseCommissionTip3DescSuffix") }}
        </h3>
        <p v-if="props.variant === 'wallet'"></p>
      </div>

      <van-button
        block
        round
        type="primary"
        class="purchase-commission-dialog__confirm"
        @click="showPurchaseCommissionPopup = false"
      >
        {{ t("invite.purchaseCommissionConfirm") }}
      </van-button>
    </section>
  </van-popup>

  <van-popup
    v-model:show="showConsumptionCommissionPopup"
    teleport="body"
    position="center"
    round
    class="consumption-commission-popup"
    overlay-class="purchase-commission-overlay"
    :close-on-click-overlay="false"
  >
    <section class="purchase-commission-dialog">
      <h2>{{ t("invite.consumptionCommissionTitle") }}</h2>
      <p class="purchase-commission-dialog__intro">
        {{ t("invite.consumptionCommissionIntro") }}
      </p>

      <div
        class="purchase-commission-dialog__item purchase-commission-dialog__item--inline"
      >
        <p>
          {{ t("invite.consumptionCommissionTip1Desc") }}
        </p>
      </div>

      <van-button
        block
        round
        type="primary"
        class="purchase-commission-dialog__confirm"
        @click="showConsumptionCommissionPopup = false"
      >
        {{ t("invite.purchaseCommissionConfirm") }}
      </van-button>
    </section>
  </van-popup>

  <van-popup
    v-model:show="showMonthlyCommissionPopup"
    teleport="body"
    position="center"
    round
    class="consumption-commission-popup"
    overlay-class="purchase-commission-overlay"
    :close-on-click-overlay="false"
  >
    <section class="purchase-commission-dialog">
      <h2>{{ t("invite.monthlyCommissionTitle") }}</h2>
      <p class="purchase-commission-dialog__intro">
        {{ t("invite.monthlyCommissionIntro") }}
      </p>

      <div
        v-for="tip in monthlyCommissionTips"
        :key="tip.title"
        class="purchase-commission-dialog__item purchase-commission-dialog__item--inline"
      >
        <p>
          <span data-strong>{{ tip.title }}{{ monthlyTipSeparator }}</span
          >{{ tip.desc }}
        </p>
      </div>

      <van-button
        block
        round
        type="primary"
        class="purchase-commission-dialog__confirm"
        @click="showMonthlyCommissionPopup = false"
      >
        {{ t("invite.purchaseCommissionConfirm") }}
      </van-button>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  tagText: {
    type: String,
    default: "",
  },
  joinText: {
    type: String,
    default: "",
  },
  variant: {
    type: String,
    default: "wallet",
  },
  preserveProgressSpace: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["join-partner"]);
const { t, locale } = useI18n();
const showPurchaseCommissionPopup = ref(false);
const showConsumptionCommissionPopup = ref(false);
const showMonthlyCommissionPopup = ref(false);
const card = computed(() => props.card || {});
const popupSeparator = computed(() =>
  props.variant === "wallet" ? "——" : "--",
);
const monthlyTipSeparator = computed(() =>
  String(locale.value || "").startsWith("zh-") ? "：" : ": ",
);
const monthlyCommissionTips = computed(() => [
  {
    title: t("invite.monthlyCommissionTip1"),
    desc: t("invite.monthlyCommissionTip1Desc"),
  },
  {
    title: t("invite.monthlyCommissionTip2"),
    desc: t("invite.monthlyCommissionTip2Desc"),
  },
  {
    title: t("invite.monthlyCommissionTip3"),
    desc: t("invite.monthlyCommissionTip3Desc"),
  },
  {
    title: t("invite.monthlyCommissionTip4"),
    desc: t("invite.monthlyCommissionTip4Desc"),
  },
]);
const metrics = computed(() => {
  const items = Array.isArray(card.value.metrics) ? card.value.metrics : [];
  if (![3, 4].includes(Number(card.value.level))) {
    return items;
  }

  return [
    ...items.slice(0, 2),
    {
      label: t("invite.monthlyCommissionTitle"),
      value: String(card.value.monthRatePercent || "0%"),
      icon: "info-o",
      action: "monthlyCommission",
    },
    ...items.slice(2),
  ];
});

function handlePartnerJoin() {
  emit("join-partner");
}

function handleMetricClick(item) {
  if (item?.action === "purchaseCommission") {
    showPurchaseCommissionPopup.value = true;
    return;
  }
  if (item?.action === "consumptionCommission") {
    showConsumptionCommissionPopup.value = true;
    return;
  }
  if (item?.action === "monthlyCommission") {
    showMonthlyCommissionPopup.value = true;
    return;
  }
  if (typeof item?.click === "function") {
    item.click();
  }
}
</script>

<style scoped lang="scss">
.points-level {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;

  .hint {
    flex: 0 0 auto;
    font-size: 16px;
  }
}

.points-level--hidden {
  visibility: hidden;
}

.points-level-card {
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;

  &--wallet {
    font-size: 13px;

    .points-level-card_item-label {
      font-size: 14px;
    }
  }

  &--mine {
    font-size: 15px;

    .points-level-card_item-label {
      font-size: 12px;
    }
  }

  &_list {
    display: flex;
    min-width: 0;
    justify-content: space-between;
  }

  &_item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  &_item-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    max-width: 100%;
    line-height: 1.5;
    text-align: center;

    > span:first-child {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }

  &_item-icon {
    flex: 0 0 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    box-sizing: border-box;
    border: 1px solid currentColor;
    border-radius: 50%;
    color: currentColor;
    font-family: Arial, sans-serif;
    font-size: 12px;
    font-weight: 700;
    font-style: normal;
    line-height: 1;
  }

  &_item > div:last-child {
    width: 100%;
    line-height: 1.5;
    overflow-wrap: anywhere;
    font-weight: 700;
    text-align: center;
  }
}

.points-level-card__tag {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  padding: 5px 8px;
  border-radius: 0 16px 0 4px;
  font-size: 12px;
  line-height: 1;
}

.points-level-card__body {
  display: flex;
  flex-direction: column;
  padding: 16px 14px;
  box-sizing: border-box;
}

.points-level-card__title {
  display: flex;
  align-items: flex-start;
  gap: 6px;

  [data-strong] {
    display: block;
    font-size: 14px;
    font-weight: 700;
    line-height: 19px;
    white-space: nowrap;
  }

  &--partner {
    padding: 2px 0 17px;
  }
}

.points-level-card__join {
  margin-top: 0;
  margin-left: 8px;
  padding: 3px 16px;
  border: 0;
  border-radius: 16px;
  background: #716b5f;
  color: #ffeaa3;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
}

.points-level-card__icon {
  width: 23px;
  height: 19px;
  flex: 0 0 23px;
  object-fit: contain;

  :deep(.van-image__img) {
    object-fit: contain;
  }
}

.points-level-card__bar {
  height: 4px;
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  margin-right: 0;
  border-radius: 999px;

  i {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--wp-color-brand-1);
  }
}

.points-level-card__foot {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 8px;
  margin-top: 10px;
}

.points-level-card__foot--four {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.points-level-card--wallet .points-level-card__foot--four {
  gap: 4px;

  .points-level-card_item-label {
    font-size: 12px;
  }

  .points-level-card_item > div:last-child {
    white-space: nowrap;
  }
}

.points-level-card_item-value--zero {
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 4px;
}

:global(.purchase-commission-popup.van-popup--center),
:global(.consumption-commission-popup.van-popup--center) {
  width: calc(100% - 24px);
  max-width: 344px;
  max-height: calc(100vh - 48px);
  left: var(--app-shell-left) !important;
  right: calc(
    100vw - var(--app-shell-left) - var(--app-shell-width)
  ) !important;
  margin: 0 auto !important;
  transform: translateY(-50%) !important;
  overflow: hidden;
  border-radius: 24px;
  background: #ffffff;
}

:global(.purchase-commission-overlay) {
  width: var(--app-shell-width);
  max-width: 100vw;
  left: var(--app-shell-left);
  right: auto;
}

.purchase-commission-dialog {
  padding: 24px 24px 28px;
  overflow-y: auto;
  box-sizing: border-box;
  background: #ffffff;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 18px;
    font-weight: 700;
  }

  &__intro {
    margin: 16px 0 18px;
    color: #000000;
    font-size: 16px;
    font-weight: 500;
  }

  &__item {
    position: relative;
    padding-left: 16px;

    &::before {
      position: absolute;
      top: 5px;
      left: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #000000;
      content: "";
    }

    h3 {
      margin: 0;
      color: #333333;
      font-size: 15px;
      font-weight: 400;
    }

    p {
      margin: 2px 0 10px;
      color: #333;
      font-weight: 500;
      font-size: 14px;
      [data-strong] {
        color: #000;
        font-weight: 700;
      }
    }
  }

  &__item--inline p {
    margin: 0 0 10px;
    line-height: 24px;
  }

  &__emphasis {
    color: #000000;
    font-weight: 700;
  }

  &__time {
    font-weight: 700;
  }

  &__confirm {
    margin-top: 28px;
    border: 0;
    background: var(--wp-color-theme);
    font-size: 18px;
    font-weight: 500;
  }
}
</style>
